import React from "react";
import { HISTORY_FIELD_LABELS } from "@common/constants/history-source-types";
import { pluralizeWithCount } from "@common/helpers/string";
import { joinWithAnd } from "@helpers/joinWithAnd";
import { PreviewField } from "../hooks/useHistorySourceRevert";

interface RevertPreviewQuestionProps {
    fallbackCount: number;
    previewCount: number | undefined;
    previewFields: PreviewField[] | undefined;
    primaryFields: string[];
    isPreviewLoading: boolean;
}

const FIELD_LABEL_MAP = new Map<string, string>(
    Object.entries(HISTORY_FIELD_LABELS),
);

function getFieldLabel(field: string): string | null {
    return FIELD_LABEL_MAP.get(field) ?? null;
}

function capitalize(label: string): string {
    if (label.length === 0) {
        return label;
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function buildSecondaryBullet(field: PreviewField): string | null {
    const label = getFieldLabel(field.field);
    if (!label) {
        return null;
    }
    return `${capitalize(label)} for ${pluralizeWithCount("transaction", field.transactionCount)}`;
}

function buildFallbackSentence(field: PreviewField): string | null {
    const label = getFieldLabel(field.field);
    if (!label) {
        return null;
    }
    return (
        `${capitalize(label)} will be reverted to its previous ${label} ` +
        `for ${pluralizeWithCount("transaction", field.transactionCount)}.`
    );
}

export const RevertPreviewQuestion: React.FC<RevertPreviewQuestionProps> = ({
    fallbackCount,
    previewCount,
    previewFields,
    primaryFields,
    isPreviewLoading,
}) => {
    if (isPreviewLoading) {
        return <div>Loading affected changes…</div>;
    }

    const totalCount = previewCount ?? fallbackCount;
    const allFields = previewFields ?? [];
    const primarySet = new Set(primaryFields);

    const primaryRows = allFields.filter((row) => primarySet.has(row.field));
    const secondaryRows = allFields.filter((row) => !primarySet.has(row.field));

    const primaryLabels = primaryRows
        .map((row) => getFieldLabel(row.field))
        .filter((label): label is string => label !== null);

    if (primaryLabels.length === 0) {
        const sentences = allFields
            .map((field) => buildFallbackSentence(field))
            .filter((s): s is string => s !== null);
        return (
            <div>
                <p>
                    {pluralizeWithCount("transaction", totalCount)} will be
                    affected.
                </p>
                {sentences.length > 0 && (
                    <ul>
                        {sentences.map((sentence) => (
                            <li key={sentence}>{sentence}</li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }

    const primaryLabel = capitalize(joinWithAnd(primaryLabels));
    const transactionsPhrase = pluralizeWithCount("transaction", totalCount);

    if (secondaryRows.length === 0) {
        return (
            <div>
                <p>
                    {primaryLabel} will be reverted for {transactionsPhrase}.
                </p>
            </div>
        );
    }

    const bullets = secondaryRows
        .map((row) => buildSecondaryBullet(row))
        .filter((b): b is string => b !== null);

    return (
        <div>
            <p>
                Reverting {primaryLabel} for {transactionsPhrase}.
            </p>
            {bullets.length > 0 && (
                <>
                    <p>Related changes that will also be reverted:</p>
                    <ul>
                        {bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};
